import BottomSheet from '@/components/BottomSheet/BottomSheet';
import CloseIcon from '@/components/Icon/CloseIcon';
import PlayIcon from '@/components/Icon/PlayIcon';
import SoundIcon from '@/components/Icon/SoundIcon';
import StopIcon from '@/components/Icon/StopIcon';
import IconButton from '@/components/IconButton/IconButton';
import LabelBackground from '@/components/LabelBackground/LabelBackground';
import LargeSquareBackground from '@/components/LargeSquareBackground/LargeSquareBackground';
import { motion } from 'framer-motion';
import { FC, useEffect, useRef, useState } from 'react';

const AudioPlayer: FC<{ src: string; mimeType: string; altText?: string }> = ({
	src,
	mimeType,
	altText,
}) => {
	const audioRef = useRef<HTMLAudioElement>(null);

	const [isLoaded, setIsLoaded] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [audioPosition, setAudioPosition] = useState(0);
	const [audioLength, setAudioLength] = useState(0);

	useEffect(() => {
		if (!audioRef.current || !isLoaded) return;
		if (isPlaying) {
			audioRef.current.play();
		} else {
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
		}
	}, [isLoaded, isPlaying]);

	useEffect(() => {
		if (!audioRef.current) return;
		if (!isLoaded) audioRef.current.load();

		const updateAudioPosition = () => {
			setAudioPosition(audioRef.current?.currentTime ?? 0);
			setAudioLength(audioRef.current?.duration ?? 0);
			// Stop audio when it reaches the end
			if (
				(audioRef.current?.currentTime ?? 0) >=
				(audioRef.current?.duration ?? 0)
			) {
				setIsPlaying(false);
			}
		};
		audioRef.current.addEventListener('timeupdate', updateAudioPosition);
		const audioElement = audioRef.current;
		return () =>
			audioElement?.removeEventListener(
				'timeupdate',
				updateAudioPosition,
			);
	}, [isLoaded]);

	const buttonLabel = isPlaying ? 'Stoppen' : 'Afspelen';

	const isTranscriptionAvailable = altText !== undefined;

	return (
		<div className="relative shrink-0 p-4">
			<audio
				ref={audioRef}
				onCanPlayThrough={(e) => {
					const audioElement = e.target as HTMLAudioElement;
					setAudioLength(audioElement.duration);
					setIsLoaded(true);
				}}>
				<source
					src={src}
					type={mimeType}
					onCanPlayThrough={(e) => {
						const audioElement = e.target as HTMLAudioElement;
						setAudioLength(audioElement.duration);
						setIsLoaded(true);
					}}
				/>
			</audio>
			<LargeSquareBackground className="text-accent" />
			<div className="grid items-center justify-center gap-2">
				<div className="mx-auto">
					<IconButton
						aria-label={buttonLabel}
						title={buttonLabel}
						onClick={() => setIsPlaying(!isPlaying)}
						icon={
							<div className="relative">
								<SoundIcon className="absolute -end-4 -top-5 size-6 rotate-6 text-accentCompat lg:-end-4 lg:-top-7" />
								{isPlaying ? <StopIcon /> : <PlayIcon />}
							</div>
						}
						disabled={!isLoaded}
						animate={{
							opacity: isLoaded ? 1 : 0.5,
						}}
					/>
				</div>
				{Math.floor(audioLength) > 0 && (
					<motion.p
						initial={{ height: 0 }}
						animate={{
							height: audioPosition !== audioLength ? 'auto' : 0,
						}}
						className="relative mx-auto flex w-full max-w-24 justify-between overflow-hidden px-1 text-accentCompat">
						<span>{Math.floor(audioPosition)}s </span>
						<span>{Math.floor(audioLength)}s</span>
					</motion.p>
				)}
				{isTranscriptionAvailable && (
					<BottomSheet
						className="-mb-5"
						triggerButton={{
							label: 'Transcriptie',
							content: (
								<div className="relative text-white">
									<LabelBackground className="text-black/90" />
									<div className="relative flex items-center gap-2 px-2 py-1 font-brand text-xl">
										Transcriptie
									</div>
								</div>
							),
						}}
						message={altText}
						options={[
							{
								label: 'Sluiten',
								icon: <CloseIcon />,
							},
						]}
					/>
				)}
			</div>
		</div>
	);
};

export default AudioPlayer;
